import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import FullWidgetLink from '../FullWidgetLink';
import { getNavItems, getBackLink, getPageType } from '../../utils/navigation';
import { getFeature, FEATURE__SHOWCASE, FEATURE__AUCTIONEERS_ONLY } from '../../utils/features';
import { prefetchAuctioneers, prefetchFilteredAuctions } from '../../gateways/auctionlook_api';

import './WidgetNav.css';

const WidgetNav = ({ title }) => {
    const { pathname } = useLocation();
    const lockToAuctioneer = useSelector(state => state.lockToAuctioneer);
    const lastListUrl = useSelector(state => state.lastListUrl);
    const associationState = useSelector(state => state.associationState);
    const showcase = getFeature(FEATURE__SHOWCASE);
    const auctioneersOnly = getFeature(FEATURE__AUCTIONEERS_ONLY);

    const pageType = getPageType(pathname);
    const isDetailPage = pageType === 'auction' || pageType === 'auctioneer';

    const navItems = getNavItems(pathname, { lockToAuctioneer, showcase, auctioneersOnly });
    const backLink = getBackLink(pathname, { lockToAuctioneer, lastListUrl });

    // Prefetch auctioneers page 1 on hover
    var handleAuctioneersHover = useCallback(function() {
        prefetchAuctioneers({ association: associationState }, 1);
    }, [associationState]);

    // Prefetch auctions page 1 on hover
    var handleAuctionsHover = useCallback(function() {
        prefetchFilteredAuctions({ filters: { association: associationState }, page: 1, pageSize: 20 });
    }, [associationState]);

    // Don't render nav in showcase mode
    if (showcase) return null;

    // Don't render if auctioneers only and not on a detail page
    if (auctioneersOnly && !isDetailPage) return null;

    // Don't render if locked to auctioneer and on home page
    if (lockToAuctioneer && !isDetailPage) return null;

    return (
        <nav className="al-widget-nav">
            <div className="al-widget-nav-content">
                {/* Back button on detail pages */}
                {isDetailPage && backLink && (
                    <FullWidgetLink
                        to={backLink.url}
                        className="al-nav-back"
                    >
                        <ArrowLeft size={20} />
                        <span className="al-nav-back-text">{backLink.label}</span>
                    </FullWidgetLink>
                )}

                {/* Nav tabs on list pages */}
                {!isDetailPage && navItems.length > 0 && (
                    <div className="al-nav-tabs">
                        {navItems.map(function(item) {
                            var isAuctioneers = item.url.indexOf('/auctioneers') === 0;
                            var isAuctions = item.url.indexOf('/auctions') === 0 || item.url === '/';
                            var hoverHandler = isAuctioneers ? handleAuctioneersHover : (isAuctions ? handleAuctionsHover : undefined);
                            return (
                                <FullWidgetLink
                                    key={item.url}
                                    to={item.url}
                                    className={'al-nav-tab' + (item.active ? ' al-nav-tab--active' : '')}
                                    onMouseEnter={hoverHandler}
                                >
                                    {item.label}
                                </FullWidgetLink>
                            );
                        })}
                    </div>
                )}

                {/* Page title on detail pages */}
                {isDetailPage && title && (
                    <h1 className="al-nav-title">{title}</h1>
                )}
            </div>
        </nav>
    );
};

WidgetNav.propTypes = {
    title: PropTypes.string
};

export default WidgetNav;
